// src/pages/ForgotPassword.js
import React, { useState, useRef } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../config/firebase.config';
import './ForgotPassword.css';

function ForgotPassword({ onBackToLogin }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const emailRef = useRef();

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    const email = emailRef.current?.value;
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess('Password reset email sent! Check your inbox.');
      
      // Clear the form
      if (emailRef.current) emailRef.current.value = '';
      
      // Redirect to login after 5 seconds
      setTimeout(() => {
        onBackToLogin();
      }, 5000);
      
    } catch (error) {
      console.error('Password reset error:', error);
      
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again later');
      } else {
        setError('Failed to send reset email. Please try again');
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="forgot-page">
      <div className="forgot-container">
        {/* Left Panel - Illustration */}
        <div className="forgot-left-panel">
          <div className="illustration-content">
            <div className="brand-header">
              <div className="brand-icon">🏢</div>
              <h1>Srinivasa Flats</h1>
              <p>Community Management System</p>
            </div>
            
            <div className="reset-illustration">
              <div className="lock-icon">🔐</div>
              <h3>Password Reset</h3>
              <p>We'll send you instructions to reset your password</p>
            </div>
            
            <div className="security-features">
              <div className="security-item">
                <span className="security-icon">🔒</span>
                <span>Secure password reset link</span>
              </div>
              <div className="security-item">
                <span className="security-icon">📧</span>
                <span>Email verification required</span>
              </div>
              <div className="security-item">
                <span className="security-icon">⏱️</span>
                <span>Link expires in 1 hour</span>
              </div>
            </div>
            
            <div className="brand-footer">
              <p>Powered by <strong>STRONARIX</strong></p>
            </div>
          </div>
        </div>
        
        {/* Right Panel - Reset Form */}
        <div className="forgot-right-panel">
          <div className="forgot-form-container">
            <div className="form-header">
              <h2>Forgot Password?</h2>
              <p>No worries, we'll send you reset instructions</p>
            </div>
            
            <form className="forgot-form" onSubmit={handlePasswordReset}>
              {error && (
                <div className="alert-error">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}
              
              {success && (
                <div className="alert-success">
                  <span className="success-icon">✅</span>
                  {success}
                </div>
              )}
              
              <div className="form-group">
                <label>Email Address</label>
                <input
                  ref={emailRef}
                  type="email"
                  className="form-control"
                  placeholder="Enter your registered email"
                  required
                  autoFocus
                />
                <p className="form-help">
                  Enter the email address associated with your account
                </p>
              </div>
              
              <button 
                type="submit" 
                className="btn-primary" 
                disabled={loading || success}
              >
                {loading ? 'Sending...' : 'Send Reset Email'}
              </button>
              
              <div className="form-divider">
                <span>Remember your password?</span>
              </div>
              
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={onBackToLogin}
              >
                Back to Login
              </button>
            </form>
            
            <div className="help-section">
              <p className="help-title">Need help?</p>
              <p className="help-text">
                If you don't receive an email, check your spam folder or 
                contact your administrator for assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;